// 15C — Percorso ESG Guidato: ogni passo mostra lo stato REALE (anagrafica,
// visura, bollette, questionario, documenti, motori, report). Nessuna
// percentuale o percorso simulato: i dati arrivano dall'endpoint /percorso.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  Circle,
  Loader,
  Compass,
  ArrowRight,
  AlertTriangle,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useTenant } from "@/context/TenantContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const STATUS_UI = {
  DONE: { icon: CheckCircle2, cls: "text-green-700", badge: "bg-green-50 text-green-800 border-green-300", label: "Completato" },
  IN_PROGRESS: { icon: Loader, cls: "text-amber-600", badge: "bg-amber-50 text-amber-800 border-amber-300", label: "In corso" },
  REVIEW_REQUIRED: { icon: AlertTriangle, cls: "text-red-700", badge: "bg-red-50 text-red-800 border-red-300", label: "Da riesaminare" },
  TODO: { icon: Circle, cls: "text-slate-300", badge: "bg-slate-100 text-slate-500 border-slate-300", label: "Da fare" },
};

export default function PercorsoGuidato() {
  const { companyId } = useTenant();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const { data: d } = await api.get(`/companies/${companyId}/percorso`);
      setData(d);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="text-sm text-red-700" data-testid="percorso-error">{error}</p>;
  if (!data) return <p className="text-sm text-slate-600" data-testid="percorso-loading">Caricamento…</p>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="percorso-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <Compass className="w-7 h-7 text-green-700" aria-hidden="true" />
          Il tuo percorso ESG
        </h1>
        <p className="text-sm text-slate-600 mt-1" data-testid="percorso-progress">
          {data.done} di {data.total} passi completati
          {data.period_year ? ` · esercizio ${data.period_year}` : ""}.
          Carica quello che hai: al resto pensa ESG-PMI.
        </p>
        {data.questionnaire_summary?.effective_status === "INTEGRATION_REQUIRED" && (
          <p className="mt-2 text-sm font-semibold text-red-800" data-testid="percorso-effective-status">
            Da integrare · {data.questionnaire_summary.blocking_inconsistencies} incongruenze bloccanti
          </p>
        )}
        {data.questionnaire_summary?.effective_status === "READY_FOR_RESUBMISSION" && (
          <p className="mt-2 text-sm font-semibold text-amber-800" data-testid="percorso-effective-status">
            Pronto per nuovo invio
          </p>
        )}
      </div>

      <div className="space-y-3">
        {data.steps.map((step, i) => {
          const ui = STATUS_UI[step.status];
          const Icon = ui.icon;
          return (
            <Card key={step.code}
              className={`border-slate-200 ${step.status === "TODO" ? "opacity-90" : ""}`}
              data-testid={`percorso-step-${step.code}`}>
              <CardContent className="flex flex-col gap-4 pt-4 sm:flex-row sm:items-start">
                <div className="flex flex-col items-center pt-0.5">
                  <Icon className={`w-6 h-6 ${ui.cls}`} aria-hidden="true" />
                  {i < data.steps.length - 1 && <div className="w-px flex-1 bg-slate-200 mt-1" />}
                </div>
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-slate-900">{i + 1}. {step.title}</p>
                    <Badge variant="outline" className={`text-[10px] ${ui.badge}`}
                      data-testid={`percorso-status-${step.code}`}>{ui.label}</Badge>
                  </div>
                  <p className="text-sm text-slate-600 break-words">{step.description}</p>
                  <p className="text-xs text-slate-500 break-words" data-testid={`percorso-detail-${step.code}`}>
                    {step.detail}
                  </p>
                </div>
                {step.status !== "DONE" && (
                  <Button size="sm" variant="outline" className="self-start shrink-0"
                    onClick={() => navigate(step.link)}
                    data-testid={`percorso-cta-${step.code}`}>
                    {step.cta} <ArrowRight className="w-3.5 h-3.5 ml-1" aria-hidden="true" />
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
